import { Component } from '@theme/component';

/** Native navigation enhancements; links and details work without this module. */
class LancieNavigation extends Component {
  #abort;
  #opener;
  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    document.addEventListener('click', (event) => {
      if (event.target instanceof Node && !this.contains(event.target)) this.#closeDropdowns();
    }, options);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#closeDropdowns(true);
    }, options);
    document.addEventListener('scroll', () => this.#updateScroll(), { ...options, capture: true, passive: true });
    window.addEventListener('resize', () => this.#updateScroll(), { ...options, passive: true });
    window.matchMedia('(min-width: 841px)').addEventListener('change', (event) => {
      if (event.matches) this.closeMenu();
    }, options);
    const menu = this.querySelector('dialog');
    menu?.addEventListener('close', () => {
      this.querySelector('.lu-header__burger')?.setAttribute('aria-expanded', 'false');
      if (this.#opener instanceof HTMLElement) this.#opener.focus({ preventScroll: true });
    }, options);
    menu?.addEventListener('click', (event) => {
      if (event.target !== menu) return;
      const rect = menu.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeMenu();
    }, options);
    this.#updateScroll();
  }
  disconnectedCallback() {
    this.#abort?.abort();
    super.disconnectedCallback();
  }
  /** @param {MouseEvent} event */
  openMenu(event) {
    const menu = this.querySelector('dialog');
    if (!(menu instanceof HTMLDialogElement)) return;
    this.#opener = event.target instanceof Element ? event.target.closest('button') : null;
    if (!menu.open) menu.showModal();
    this.querySelector('.lu-header__burger')?.setAttribute('aria-expanded', 'true');
  }
  closeMenu() {
    const menu = this.querySelector('dialog');
    if (menu instanceof HTMLDialogElement && menu.open) menu.close();
  }
  #closeDropdowns(restoreFocus = false) {
    const active = document.activeElement;
    let focusTarget;
    for (const details of this.querySelectorAll('.lu-header__desktop details[open], .lu-header__search[open]')) {
      if (details instanceof HTMLDetailsElement) {
        if (restoreFocus && active instanceof Node && details.contains(active)) focusTarget = details.querySelector('summary');
        details.open = false;
      }
    }
    if (focusTarget instanceof HTMLElement) focusTarget.focus();
  }
  #updateScroll() {
    // The native theme scrolls its wrapper only at desktop widths.
    const scrollTop = window.matchMedia('(min-width: 990px)').matches
      ? document.querySelector('.page-wrapper')?.scrollTop || window.scrollY
      : window.scrollY;
    this.classList.toggle('is-scrolled', scrollTop > 40);
    const announcement = document.querySelector('lancie-announcement');
    const offset = scrollTop > 40 && announcement instanceof HTMLElement ? announcement.offsetHeight : 0;
    document.documentElement.style.setProperty('--lu-ann-offset', `${-offset}px`);
    document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
    document.body.style.setProperty('--header-group-height', `${this.offsetHeight + (announcement instanceof HTMLElement ? announcement.offsetHeight : 0)}px`);
  }
}
class LancieAnnouncement extends Component {
  dismiss() {
    this.hidden = true;
    this.style.display = 'none';
    document.documentElement.style.setProperty('--lu-ann-offset', '0px');
  }
}
class LancieFooter extends Component {
  #abort;
  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const media = window.matchMedia('(min-width: 561px)');
    const update = () => {
      for (const menu of this.querySelectorAll('details')) {
        if (menu instanceof HTMLDetailsElement) menu.open = media.matches;
      }
    };
    media.addEventListener('change', update, { signal: this.#abort.signal });
    update();
  }
  disconnectedCallback() {
    this.#abort?.abort();
    super.disconnectedCallback();
  }
}
for (const [name, definition] of [['lancie-navigation', LancieNavigation], ['lancie-announcement', LancieAnnouncement], ['lancie-footer', LancieFooter]]) {
  if (!customElements.get(name)) customElements.define(name, definition);
}

/** A real collection finder: merchant-selected Shopify collections, no simulated products. */
class LancieFinder extends Component {
  /** @param {SubmitEvent} event */
  recommend(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement) || !form.reportValidity()) return;
    const chosen = new FormData(form).get('intention');
    const results = this.querySelector('.lu-quiz__result');
    if (!(results instanceof HTMLElement) || typeof chosen !== 'string') return;
    let found = false;
    for (const article of results.querySelectorAll('[data-recommendation]')) {
      if (!(article instanceof HTMLElement)) continue;
      article.hidden = article.dataset.recommendation !== chosen;
      if (!article.hidden) found = true;
    }
    if (!found) return;
    results.hidden = false;
    form.hidden = true;
    results.focus();
  }
  restart() {
    const form = this.querySelector('form');
    const results = this.querySelector('.lu-quiz__result');
    if (results instanceof HTMLElement) results.hidden = true;
    if (form instanceof HTMLFormElement) {
      form.hidden = false;
      const first = form.querySelector('input');
      if (first instanceof HTMLInputElement) first.focus();
    }
  }
}
if (!customElements.get('lancie-finder')) customElements.define('lancie-finder', LancieFinder);

/** One gentle entrance per content group. Content never depends on animation to be visible. */
class LancieMotion extends Component {
  #abort;
  #observer;
  #seen = new WeakSet();
  #animations = new Map();
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #targets = [
    '.lu-hero__content', '.lu-palette__track', '.lu-intro > .lu-wrap',
    '.lu-cards__heading', '.lu-cards__grid', '.lu-views__heading', '.lu-views__grid',
    '.lu-split__media', '.lu-split__copy', '.lu-chakras__media', '.lu-chakras__copy', '.lu-notes > .lu-wrap', '.lu-journal__card',
    '.lu-editorial__header', '.lu-editorial__body', '.lu-editorial__links',
    '.lu-collection-head__copy', '.lu-contact__grid', '.lu-faq__grid', '.lu-quiz > .lu-wrap',
    'main h1', '.blog-post-item--featured', '.lu-footer__grid',
  ].join(',');

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.#motion.addEventListener('change', () => this.#setup(), options);
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof Element) this.#observe(event.target);
    }, options);
    document.addEventListener('shopify:section:unload', (event) => {
      if (!(event.target instanceof Element)) return;
      for (const target of event.target.querySelectorAll(this.#targets)) this.#observer?.unobserve(target);
      for (const [target, animation] of this.#animations) {
        if (event.target.contains(target)) animation.cancel();
      }
    }, options);
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#stop();
    super.disconnectedCallback();
  }

  #stop() {
    this.#observer?.disconnect();
    this.#observer = undefined;
    for (const animation of this.#animations.values()) animation.cancel();
    this.#animations.clear();
  }

  #setup() {
    this.#stop();
    // Editing, reduced motion, disabled settings, and unsupported browsers keep normal rendering.
    if (this.dataset.enabled !== 'true' || this.#motion.matches || window.Shopify?.designMode || !window.IntersectionObserver) return;
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const target = entry.target;
        this.#observer?.unobserve(target);
        this.#seen.add(target);
        if (target.contains(document.activeElement)) continue;
        const duration = Math.min(900, Math.max(300, Number(this.dataset.duration) || 600));
        const animation = target.animate([
          { opacity: 0, transform: 'translateY(16px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ], { duration, easing: 'cubic-bezier(.22, 1, .36, 1)' });
        this.#animations.set(target, animation);
        animation.finished.catch(() => {}).finally(() => {
          if (this.#animations.get(target) === animation) this.#animations.delete(target);
        });
      }
    }, { threshold: 0.05, rootMargin: '0px 0px -24px 0px' });
    this.#observe(document);
  }

  #observe(root) {
    if (!this.#observer) return;
    for (const target of root.querySelectorAll(this.#targets)) {
      // Do not animate nested titles twice or run an entrance on already focused content.
      if (this.#seen.has(target) || target.parentElement?.closest(this.#targets)) continue;
      this.#observer.observe(target);
    }
  }
}
if (!customElements.get('lancie-motion')) customElements.define('lancie-motion', LancieMotion);

/** Keep the existing wishlist app functional while its empty-state art uses Shopify Files. */
class LancieMedia extends Component {
  #observer;
  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }
  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }
  disconnectedCallback() {
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }
  #setup() {
    this.#observer?.disconnect();
    const url = this.dataset.wishlistImage;
    if (!url) return;
    const selector = '.nm-wishlist-empty-state img';
    const scan = (root) => {
      if (!(root instanceof Element)) return;
      const images = root.matches(selector) ? [root] : root.querySelectorAll(selector);
      for (const image of images) {
        if (image instanceof HTMLImageElement && image.getAttribute('src') !== url) image.setAttribute('src', url);
      }
    };
    scan(document.body);
    this.#observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') scan(mutation.target);
        else for (const node of mutation.addedNodes) scan(node);
      }
    });
    this.#observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
  }
}
if (!customElements.get('lancie-media')) customElements.define('lancie-media', LancieMedia);

/** Accessibility and theme-editor lifecycle around the existing native slideshow. */
class LancieHero extends HTMLElement {
  #abort;
  #observer;
  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    customElements.whenDefined('slideshow-component').then(() => {
      if (signal.aborted) return;
      const slider = this.querySelector('slideshow-component');
      if (!slider) return;
      const syncSlides = () => {
        for (const slide of slider.querySelectorAll('slideshow-slide')) slide.inert = slide.getAttribute('aria-hidden') === 'true';
      };
      this.#observer?.disconnect();
      this.#observer = new MutationObserver(syncSlides);
      this.#observer.observe(slider, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
      syncSlides();
      const pauseForMotion = () => { if (motion.matches || window.Shopify?.designMode) slider.pause(); };
      motion.addEventListener('change', pauseForMotion, { signal });
      pauseForMotion();
      slider.addEventListener('focusin', () => slider.pause(), { signal });
      // Pointer pause/play controls remain native; keyboard focus never rotates away.
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target instanceof Element ? event.target.closest('slideshow-slide') : null;
        if (!slide) return;
        const index = [...slider.querySelectorAll('slideshow-slide')].indexOf(slide);
        slider.pause();
        slider.select(index, undefined, { animate: false });
      }, { signal });
    });
  }
  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.querySelector('slideshow-component')?.pause?.();
  }
}
if (!customElements.get('lancie-hero')) customElements.define('lancie-hero', LancieHero);
